import React, { useEffect, useState, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  Alert,
  ToggleButton,
  ToggleButtonGroup,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Tooltip,
  Button,
  Collapse,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber';
import PeopleIcon from '@mui/icons-material/People';
import PaymentIcon from '@mui/icons-material/Payment';
import StorefrontIcon from '@mui/icons-material/Storefront';
import CachedIcon from '@mui/icons-material/Cached';
import SettingsIcon from '@mui/icons-material/Settings';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import {
  ComposedChart,
  Area,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  Legend,
  Cell,
  PieChart,
  Pie,
} from 'recharts';
import { useAuth } from '../contexts/AuthContext';
import { fetchRevenueStats, fetchCacheStatus, rebuildStats } from '../api/stats';
import { useI18n } from '../i18n';

// Color palette for charts (violet/purple theme matching reference)
const CHART_COLORS = {
  primary: '#7C4DFF',
  secondary: '#B388FF',
  tertiary: '#E1BEE7',
  accent: '#651FFF',
  gradient: ['#7C4DFF', '#B388FF', '#E1BEE7', '#D1C4E9', '#EDE7F6'],
};

const CURRENCY_COLORS = {
  RUB: '#7C4DFF',
  USD: '#00BCD4',
  TRY: '#FF5722',
  CNY: '#F44336',
  KZT: '#4CAF50',
};

const PERIODS = [
  { value: 'today', label: 'Сегодня' },
  { value: '7', label: '7 дней' },
  { value: '30', label: '30 дней' },
  { value: 'all', label: 'Всё время' },
  { value: 'custom', label: 'Период' },
];

const MONTHS = [
  { value: 1, label: 'Янв' },
  { value: 2, label: 'Фев' },
  { value: 3, label: 'Мар' },
  { value: 4, label: 'Апр' },
  { value: 5, label: 'Май' },
  { value: 6, label: 'Июн' },
  { value: 7, label: 'Июл' },
  { value: 8, label: 'Авг' },
  { value: 9, label: 'Сен' },
  { value: 10, label: 'Окт' },
  { value: 11, label: 'Ноя' },
  { value: 12, label: 'Дек' },
];

// Glassmorphism card style
const glassCardSx = {
  background: 'linear-gradient(135deg, rgba(255,255,255,0.9) 0%, rgba(248,250,255,0.95) 100%)',
  backdropFilter: 'blur(20px)',
  borderRadius: 4,
  border: '1px solid rgba(124, 77, 255, 0.1)',
  boxShadow: '0 8px 32px rgba(124, 77, 255, 0.08)',
  p: 3,
};

// Format numbers with thousands separator
const formatNumber = (num, locale = 'ru-RU') => {
  if (num === null || num === undefined) return '0';
  return new Intl.NumberFormat(locale).format(num);
};

// Format currency amount
const formatCurrency = (amount, currency, locale = 'ru-RU') => {
  return `${formatNumber(amount, locale)} ${currency}`;
};

export default function StatisticsPage() {
  const { user } = useAuth();
  const token = user?.api_token || '';
  const { t, lang } = useI18n();
  const dateLocale = lang === 'ru' ? 'ru-RU' : lang === 'tr' ? 'tr-TR' : 'en-US';

  const periodOptions = useMemo(
    () => PERIODS.map((p) => ({ ...p, label: t(p.label) })),
    [t]
  );
  const monthOptions = useMemo(
    () => MONTHS.map((m) => ({ ...m, label: t(m.label) })),
    [t]
  );
  
  const [tab, setTab] = useState(0);
  const [period, setPeriod] = useState('7');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [stats, setStats] = useState(null);
  
  // Custom date range
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  
  // Month filter
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  
  // Cache management state
  const [showCachePanel, setShowCachePanel] = useState(false);
  const [cacheStatus, setCacheStatus] = useState(null);
  const [cacheLoading, setCacheLoading] = useState(false);
  const [rebuildLoading, setRebuildLoading] = useState(false);
  const [rebuildResult, setRebuildResult] = useState(null);

  const loadStats = useCallback(async () => {
    if (!token) return;
    
    let effectivePeriod = period;
    let effectiveDateFrom = dateFrom;
    let effectiveDateTo = dateTo;
    
    // If month is selected, calculate date range for that month
    if (selectedMonth !== null) {
      effectivePeriod = 'custom';
    
      const year = selectedYear;
      const month = selectedMonth;
    
      const firstDay = new Date(year, month - 1, 1);
      const lastDay  = new Date(year, month, 0);
    
      const pad = n => String(n).padStart(2, '0');
    
      effectiveDateFrom = `${year}-${pad(month)}-01`;
      effectiveDateTo   = `${year}-${pad(month)}-${pad(lastDay.getDate())}`;
    }
    
    // For custom period, require both dates
    if (effectivePeriod === 'custom' && (!effectiveDateFrom || !effectiveDateTo)) return;
    
    setLoading(true);
    setError('');
    try {
      const data = await fetchRevenueStats(token, effectivePeriod, effectiveDateFrom, effectiveDateTo);
      setStats(data);
    } catch (e) {
      setError(e.message || t('Ошибка загрузки статистики'));
    } finally {
      setLoading(false);
    }
  }, [token, period, dateFrom, dateTo, selectedMonth, selectedYear]);

  const loadCacheStatus = useCallback(async () => {
    if (!token) return;
    setCacheLoading(true);
    try {
      const data = await fetchCacheStatus(token);
      setCacheStatus(data);
    } catch (e) {
      console.error('Failed to load cache status:', e);
    } finally {
      setCacheLoading(false);
    }
  }, [token]);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  useEffect(() => {
    if (showCachePanel) {
      loadCacheStatus();
    }
  }, [showCachePanel, loadCacheStatus]);

  const handlePeriodChange = (_, newPeriod) => {
    if (newPeriod !== null) {
      setPeriod(newPeriod);
      setSelectedMonth(null); // Clear month filter when selecting a period
    }
  };

  const handleMonthChange = (_, newMonth) => {
    if (newMonth !== null) {
      setSelectedMonth(newMonth);
      setPeriod('custom'); // Switch to custom mode when selecting a month
    } else {
      setSelectedMonth(null);
    }
  };

  const handleRebuild = async () => {
    if (!token) return;
    setRebuildLoading(true);
    setRebuildResult(null);
    try {
      const result = await rebuildStats(token, true);
      setRebuildResult(result);
      loadCacheStatus();
      loadStats();
    } catch (e) {
      setRebuildResult({ success: false, message: e.message });
    } finally {
      setRebuildLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100%',
       
        borderRadius: 3,
        p: 3,
      }}
    >
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography
          variant="h4"
          sx={{
            fontWeight: 700,
            background: 'linear-gradient(135deg, #7C4DFF 0%, #B388FF 100%)',
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
          }}
        >
          {t('Статистика')}
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <ToggleButtonGroup
            value={period}
            exclusive
            onChange={handlePeriodChange}
            size="small"
            sx={{
              '& .MuiToggleButton-root': {
                border: '1px solid rgba(124, 77, 255, 0.3)',
                color: '#7C4DFF',
                px: 2,
                '&.Mui-selected': {
                  background: 'linear-gradient(135deg, #7C4DFF 0%, #B388FF 100%)',
                  color: '#fff',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #651FFF 0%, #7C4DFF 100%)',
                  },
                },
              },
            }}
          >
            {periodOptions.map((p) => (
              <ToggleButton key={p.value} value={p.value}>
                {p.label}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
          
          {/* Custom Date Range Picker */}
          <Collapse in={period === 'custom'} orientation="horizontal" sx={{ display: 'inline-flex' }}>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', ml: 1 }}>
              <CalendarMonthIcon sx={{ color: '#7C4DFF', fontSize: 20 }} />
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                style={{
                  padding: '6px 12px',
                  borderRadius: '8px',
                  border: '1px solid rgba(124, 77, 255, 0.3)',
                  background: 'rgba(255,255,255,0.9)',
                  color: '#333',
                  fontSize: '14px',
                  outline: 'none',
                  cursor: 'pointer',
                }}
              />
              <Typography sx={{ color: '#666', mx: 0.5 }}>—</Typography>
              <input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                style={{
                  padding: '6px 12px',
                  borderRadius: '8px',
                  border: '1px solid rgba(124, 77, 255, 0.3)',
                  background: 'rgba(255,255,255,0.9)',
                  color: '#333',
                  fontSize: '14px',
                  outline: 'none',
                  cursor: 'pointer',
                }}
              />
            </Box>
          </Collapse>
          
          <Tooltip title={t('Обновить')}>
            <IconButton onClick={loadStats} disabled={loading || (period === 'custom' && (!dateFrom || !dateTo))}>
              <RefreshIcon sx={{ color: '#7C4DFF' }} />
            </IconButton>
          </Tooltip>
          <Tooltip title={t('Управление кэшем')}>
            <IconButton onClick={() => setShowCachePanel(!showCachePanel)}>
              <SettingsIcon sx={{ color: showCachePanel ? '#7C4DFF' : '#888' }} />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Month Filter */}
      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2, flexWrap: 'wrap' }}>
        <Typography variant="body2" sx={{ color: '#666', fontWeight: 500 }}>
          {t('По месяцам:')}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton 
            size="small" 
            onClick={() => setSelectedYear(selectedYear - 1)}
            sx={{ color: '#7C4DFF' }}
          >
            <span style={{ fontSize: 14 }}>◀</span>
          </IconButton>
          <Typography sx={{ color: '#333', fontWeight: 600, minWidth: 50, textAlign: 'center' }}>
            {selectedYear}
          </Typography>
          <IconButton 
            size="small" 
            onClick={() => setSelectedYear(selectedYear + 1)}
            disabled={selectedYear >= new Date().getFullYear()}
            sx={{ color: '#7C4DFF' }}
          >
            <span style={{ fontSize: 14 }}>▶</span>
          </IconButton>
        </Box>
        <ToggleButtonGroup
          value={selectedMonth}
          exclusive
          onChange={handleMonthChange}
          size="small"
          sx={{
            flexWrap: 'wrap',
            '& .MuiToggleButton-root': {
              border: '1px solid rgba(124, 77, 255, 0.2)',
              color: '#7C4DFF',
              px: 1.5,
              py: 0.5,
              fontSize: '0.75rem',
              '&.Mui-selected': {
                background: 'linear-gradient(135deg, #7C4DFF 0%, #B388FF 100%)',
                color: '#fff',
                '&:hover': {
                  background: 'linear-gradient(135deg, #651FFF 0%, #7C4DFF 100%)',
                },
              },
            },
          }}
        >
          {monthOptions.map((m) => (
            <ToggleButton 
              key={m.value} 
              value={m.value}
              disabled={selectedYear === new Date().getFullYear() && m.value > new Date().getMonth() + 1}
            >
              {m.label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
        {selectedMonth && (
          <Chip
            label={t('Сбросить')}
            size="small"
            onDelete={() => {
              setSelectedMonth(null);
              setPeriod('7');
            }}
            sx={{ 
              bgcolor: 'rgba(124, 77, 255, 0.1)', 
              color: '#7C4DFF',
              '& .MuiChip-deleteIcon': { color: '#7C4DFF' }
            }}
          />
        )}
      </Box>

      {/* Cache Management Panel */}
      <Collapse in={showCachePanel}>
        <Paper sx={{ ...glassCardSx, mb: 3, border: '1px solid rgba(124, 77, 255, 0.2)' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <CachedIcon sx={{ color: '#7C4DFF' }} />
            <Typography variant="h6" sx={{ fontWeight: 700, color: '#333' }}>
              {t('Управление кэшем')}
            </Typography>
          </Box>
          
          {cacheLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
              <CircularProgress size={24} sx={{ color: '#7C4DFF' }} />
            </Box>
          ) : cacheStatus ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {/* Cache Status Info */}
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
                <Chip
                  icon={cacheStatus.has_cache ? <CheckCircleIcon /> : <WarningIcon />}
                  label={cacheStatus.has_cache ? t('Кэш активен') : t('Кэш пуст')}
                  color={cacheStatus.has_cache ? 'success' : 'warning'}
                  variant="outlined"
                />
                {cacheStatus.cached_days > 0 && (
                  <Typography variant="body2" sx={{ color: '#666' }}>
                    {t('Закэшировано дней:')} <strong>{cacheStatus.cached_days}</strong>
                  </Typography>
                )}
                {cacheStatus.last_rebuilt && (
                  <Typography variant="body2" sx={{ color: '#666' }}>
                    {t('Последнее обновление:')} <strong>{new Date(cacheStatus.last_rebuilt).toLocaleString(dateLocale)}</strong>
                  </Typography>
                )}
                {cacheStatus.is_building && (
                  <Chip label={t('Идёт сборка...')} color="info" size="small" />
                )}
              </Box>

              {/* Current request cache info */}
              {stats?._cache_info && (
                <Typography variant="caption" sx={{ color: '#888' }}>
                  {stats._cache_info.used_cache 
                    ? t('Данные из кэша (последние {days} дня считаются в реальном времени)').replace('{days}', stats._cache_info.live_days)
                    : t('Данные рассчитаны в реальном времени (кэш не использован)')}
                </Typography>
              )}

              {/* Rebuild Actions */}
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={handleRebuild}
                  disabled={rebuildLoading || cacheStatus.is_building}
                  startIcon={rebuildLoading ? <CircularProgress size={16} /> : <CachedIcon />}
                  sx={{ 
                    borderColor: '#7C4DFF', 
                    color: '#7C4DFF',
                    '&:hover': { borderColor: '#651FFF', bgcolor: 'rgba(124, 77, 255, 0.05)' }
                  }}
                >
                  {t('Обновить кэш')}
                </Button>
                <Typography variant="caption" sx={{ color: '#888' }}>
                  {t('Полная пересборка:')} <code style={{ background: '#f5f5f5', padding: '2px 6px', borderRadius: 4 }}>php cron/stats_rebuild_cache.php --full</code>
                </Typography>
              </Box>

              {/* Rebuild Result */}
              {rebuildResult && (
                <Alert severity={rebuildResult.success ? 'success' : 'error'} sx={{ mt: 1 }}>
                  {rebuildResult.message}
                  {rebuildResult.processed_days !== undefined && (
                    <span> • {t('Обработано дней:')} {rebuildResult.processed_days}</span>
                  )}
                  {rebuildResult.duration_seconds !== undefined && (
                    <span> • {t('Время:')} {rebuildResult.duration_seconds}s</span>
                  )}
                </Alert>
              )}
            </Box>
          ) : (
            <Typography color="text.secondary">
              {t('Не удалось загрузить статус кэша. Возможно, таблицы кэша ещё не созданы.')}
            </Typography>
          )}
        </Paper>
      </Collapse>

      {/* Tabs for subpages */}
      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        sx={{
          mb: 3,
          '& .MuiTab-root': {
            fontWeight: 600,
            color: '#666',
            '&.Mui-selected': {
              color: '#7C4DFF',
            },
          },
          '& .MuiTabs-indicator': {
            backgroundColor: '#7C4DFF',
          },
        }}
      >
        <Tab label={t('Доход')} />
        <Tab style={{ opacity: 0}} label={t('Заказы')} disabled />
        <Tab style={{ opacity: 0}} label={t('Клиенты')} disabled />
      </Tabs>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
          <CircularProgress sx={{ color: '#7C4DFF' }} />
        </Box>
      ) : stats && tab === 0 ? (
        <RevenueTab stats={stats} period={period} />
      ) : (
        <Typography color="text.secondary" sx={{ textAlign: 'center', py: 6 }}>
          {t('Раздел в разработке')}
        </Typography>
      )}
    </Box>
  );
}

function RevenueTab({ stats, period }) {
  const { t, lang } = useI18n();
  const numberLocale = lang === 'ru' ? 'ru-RU' : lang === 'tr' ? 'tr-TR' : 'en-US';
  const formatNum = (value) => formatNumber(value, numberLocale);
  const formatCurr = (value, currency) => formatCurrency(value, currency, numberLocale);
  const { summary, totals_by_currency, daily_revenue, by_payment_method, by_service, completion_rates } = stats;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      {/* Summary Cards Row */}
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 2 }}>
        <SummaryCard
          icon={<TrendingUpIcon />}
          title={t('Всего заказов')}
          value={formatNum(summary?.total_orders)}
          color="#7C4DFF"
        />
        <SummaryCard
          icon={<AccountBalanceWalletIcon />}
          title={t('Пополнено (TMT)')}
          value={formatNum(summary?.total_topup_tmt)}
          color="#00BCD4"
        />
        <SummaryCard
          icon={<ConfirmationNumberIcon />}
          title={t('Билетов продано')}
          value={formatNum(summary?.total_tickets)}
          color="#FF5722"
        />
        <SummaryCard
          icon={<PeopleIcon />}
          title={t('Уникальных клиентов')}
          value={formatNum(summary?.unique_customers)}
          color="#4CAF50"
        />
        {completion_rates?.overall && (() => {
          const relevant = completion_rates.overall.completed_orders + completion_rates.overall.unpaid_orders;
          const conversion = relevant > 0 
            ? ((completion_rates.overall.completed_orders / relevant) * 100).toFixed(1)
            : 0;
          return (
            <SummaryCard
              icon={<CheckCircleIcon />}
              title={t('Конверсия')}
              value={`${conversion}%`}
              color="#8BC34A"
            />
          );
        })()}
      </Box>

      {/* Revenue by Currency Cards */}
      <Paper sx={glassCardSx}>
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: '#333' }}>
          {t('Доход по валютам')}
        </Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 2 }}>
          {totals_by_currency?.map((item) => (
            <CurrencyCard key={item.currency} data={item} />
          ))}
        </Box>
      </Paper>

      {/* Main Revenue Chart */}
      <Paper sx={glassCardSx}>
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: '#333' }}>
          {t('Динамика дохода')}
        </Typography>
        <RevenueChart data={daily_revenue} period={period} />
      </Paper>

      {/* Two column layout for breakdowns */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' }, gap: 3 }}>
        {/* Payment Methods */}
        <Paper sx={glassCardSx}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <PaymentIcon sx={{ color: '#7C4DFF' }} />
            <Typography variant="h6" sx={{ fontWeight: 700, color: '#333' }}>
              {t('По способу оплаты')}
            </Typography>
          </Box>
          <PaymentMethodChart data={by_payment_method} />
        </Paper>

        {/* Services */}
        <Paper sx={glassCardSx}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <StorefrontIcon sx={{ color: '#7C4DFF' }} />
            <Typography variant="h6" sx={{ fontWeight: 700, color: '#333' }}>
              {t('По сервисам')}
            </Typography>
          </Box>
          <ServiceChart data={by_service} />
        </Paper>
      </Box>

      {/* Completion Rates */}
      {completion_rates && (
        <Paper sx={glassCardSx}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <CheckCircleIcon sx={{ color: '#8BC34A' }} />
            <Typography variant="h6" sx={{ fontWeight: 700, color: '#333' }}>
              {t('Успешность заказов по сервисам')}
            </Typography>
          </Box>
          <CompletionRatesChart data={completion_rates} />
        </Paper>
      )}

      {/* Detailed Table */}
      <Paper sx={glassCardSx}>
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: '#333' }}>
          {t('Детализация по валютам')}
        </Typography>
        <CurrencyTable data={totals_by_currency} />
      </Paper>
    </Box>
  );
}

function SummaryCard({ icon, title, value, color }) {
  return (
    <Paper
      sx={{
        ...glassCardSx,
        p: 2.5,
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        transition: 'transform 0.2s, box-shadow 0.2s',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: '0 12px 40px rgba(124, 77, 255, 0.15)',
        },
      }}
    >
      <Box
        sx={{
          width: 56,
          height: 56,
          borderRadius: 3,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: `linear-gradient(135deg, ${color}22 0%, ${color}11 100%)`,
          color: color,
        }}
      >
        {React.cloneElement(icon, { sx: { fontSize: 28 } })}
      </Box>
      <Box>
        <Typography variant="body2" sx={{ color: '#888', fontWeight: 500, textTransform: 'uppercase', fontSize: 11 }}>
          {title}
        </Typography>
        <Typography variant="h5" sx={{ fontWeight: 700, color: '#333' }}>
          {value}
        </Typography>
      </Box>
    </Paper>
  );
}

function CurrencyCard({ data }) {
  const { t, lang } = useI18n();
  const numberLocale = lang === 'ru' ? 'ru-RU' : lang === 'tr' ? 'tr-TR' : 'en-US';
  const formatNum = (value) => formatNumber(value, numberLocale);
  const formatCurr = (value, currency) => formatCurrency(value, currency, numberLocale);
  const color = CURRENCY_COLORS[data.currency] || '#7C4DFF';
  
  return (
    <Box
      sx={{
        p: 2,
        borderRadius: 3,
        background: `linear-gradient(135deg, ${color}11 0%, ${color}05 100%)`,
        border: `1px solid ${color}22`,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <Chip
          label={data.currency}
          size="small"
          sx={{
            backgroundColor: color,
            color: '#fff',
            fontWeight: 700,
          }}
        />
        <Typography variant="caption" sx={{ color: '#888' }}>
          {formatNum(data.orders_count)} {t('Заказов')}
        </Typography>
      </Box>
      <Typography variant="h6" sx={{ fontWeight: 700, color: '#333' }}>
        {formatCurr(data.total_amount, data.currency)}
      </Typography>
      <Typography variant="body2" sx={{ color: '#666' }}>
        TMT: {formatNum(data.total_topup)}
      </Typography>
      <Typography variant="caption" sx={{ color: '#888' }}>
        {t('Билетов')}: {formatNum(data.total_tickets)}
      </Typography>
    </Box>
  );
}

function RevenueChart({ data, period }) {
  const { t, lang } = useI18n();
  const numberLocale = lang === 'ru' ? 'ru-RU' : lang === 'tr' ? 'tr-TR' : 'en-US';
  const formatNum = (value) => formatNumber(value, numberLocale);
  const dateLocale = numberLocale;
  const currencies = data?.currencies || [];
  
  // State for toggling series visibility
  const [visibleSeries, setVisibleSeries] = React.useState({
    orders: true,
    tmt: true,
  });

  // Initialize currency visibility if not set - must be before early return
  React.useEffect(() => {
    if (currencies.length === 0) return;
    setVisibleSeries(prev => {
      const newState = { ...prev };
      let changed = false;
      currencies.forEach(cur => {
        if (newState[cur] === undefined) {
          newState[cur] = true;
          changed = true;
        }
      });
      return changed ? newState : prev;
    });
  }, [currencies.join(',')]);

  const toggleSeries = (key) => {
    setVisibleSeries(prev => ({ ...prev, [key]: !prev[key] }));
  };

  if (!data?.data?.length) {
    return (
      <Typography color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
        {t('Нет данных для отображения')}
      </Typography>
    );
  }
  
  // Ensure all currency values exist for every date point (default to 0)
  const chartData = data.data.map((item) => {
    const entry = {
      ...item,
      date: new Date(item.date).toLocaleDateString(dateLocale, { day: '2-digit', month: '2-digit' }),
    };
    // Fill missing currency values with 0 to prevent line breaks
    currencies.forEach((cur) => {
      const key = `amount_${cur}`;
      if (entry[key] === undefined || entry[key] === null) {
        entry[key] = 0;
      }
    });
    return entry;
  });

  // For "all" period, sample data to avoid overcrowding
  const displayData = period === 'all' && chartData.length > 60
    ? chartData.filter((_, i) => i % Math.ceil(chartData.length / 60) === 0)
    : chartData;

  // Calculate totals for display
  const totalOrders = chartData.reduce((sum, item) => sum + (item.orders_count || 0), 0);
  const totalTMT = chartData.reduce((sum, item) => sum + (item.total_topup || 0), 0);

  // Calculate totals per currency
  const currencyTotals = {};
  currencies.forEach(cur => {
    currencyTotals[cur] = chartData.reduce((sum, item) => sum + (item[`amount_${cur}`] || 0), 0);
  });

  return (
    <Box>
      {/* Toggle controls */}
      <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        {/* Orders toggle */}
        <Chip
          size="small"
          label={`${t('Заказы')}: ${formatNum(totalOrders)}`}
          onClick={() => toggleSeries('orders')}
          sx={{
            bgcolor: visibleSeries.orders ? '#FF6B6B' : 'transparent',
            color: visibleSeries.orders ? '#fff' : '#FF6B6B',
            border: '1px solid #FF6B6B',
            fontWeight: 600,
            cursor: 'pointer',
            '&:hover': { opacity: 0.8 },
          }}
        />
        
        {/* TMT toggle */}
        <Chip
          size="small"
          label={`TMT: ${formatNum(totalTMT)}`}
          onClick={() => toggleSeries('tmt')}
          sx={{
            bgcolor: visibleSeries.tmt ? '#4ECDC4' : 'transparent',
            color: visibleSeries.tmt ? '#fff' : '#4ECDC4',
            border: '1px solid #4ECDC4',
            fontWeight: 600,
            cursor: 'pointer',
            '&:hover': { opacity: 0.8 },
          }}
        />
        
        {/* Currency toggles */}
        {currencies.map((cur, idx) => {
          const color = CURRENCY_COLORS[cur] || CHART_COLORS.gradient[idx % 5];
          return (
            <Chip
              key={cur}
              size="small"
              label={`${cur}: ${formatNum(currencyTotals[cur])}`}
              onClick={() => toggleSeries(cur)}
              sx={{
                bgcolor: visibleSeries[cur] ? color : 'transparent',
                color: visibleSeries[cur] ? '#fff' : color,
                border: `1px solid ${color}`,
                fontWeight: 600,
                cursor: 'pointer',
                '&:hover': { opacity: 0.8 },
              }}
            />
          );
        })}
      </Box>

      <ResponsiveContainer width="100%" height={350}>
        <ComposedChart data={displayData} margin={{ top: 10, right: 60, left: 0, bottom: 0 }}>
          <defs>
            {currencies.map((cur, idx) => (
              <linearGradient key={cur} id={`gradient-${cur}`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor={CURRENCY_COLORS[cur] || CHART_COLORS.gradient[idx % 5]} stopOpacity={0.6} />
                <stop offset="95%" stopColor={CURRENCY_COLORS[cur] || CHART_COLORS.gradient[idx % 5]} stopOpacity={0.05} />
              </linearGradient>
            ))}
            <linearGradient id="gradient-tmt" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#4ECDC4" stopOpacity={0.4} />
              <stop offset="95%" stopColor="#4ECDC4" stopOpacity={0.05} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="#E8E8E8" />
          <XAxis
            dataKey="date"
            tick={{ fontSize: 11, fill: '#888' }}
            tickLine={false}
            axisLine={{ stroke: '#E8E8E8' }}
          />
          {/* Left Y-axis for amounts */}
          <YAxis
            yAxisId="left"
            tick={{ fontSize: 11, fill: '#888' }}
            tickLine={false}
            axisLine={false}
            tickFormatter={(v) => formatNum(v)}
          />
          {/* Right Y-axis for orders count */}
          {visibleSeries.orders && (
            <YAxis
              yAxisId="right"
              orientation="right"
              tick={{ fontSize: 11, fill: '#FF6B6B' }}
              tickLine={false}
              axisLine={false}
              tickFormatter={(v) => formatNum(v)}
            />
          )}
          <RechartsTooltip
            contentStyle={{
              backgroundColor: 'rgba(255,255,255,0.95)',
              border: '1px solid rgba(124, 77, 255, 0.2)',
              borderRadius: 12,
              boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
            }}
            formatter={(value, name) => {
              if (name === t('Заказы')) return [formatNum(value), t('Заказов')];
              if (name === 'TMT') return [formatNum(value), t('Пополнено (TMT)')];
              return [formatNum(value), name];
            }}
          />
          
          {/* TMT area - conditional */}
          {visibleSeries.tmt && (
            <Area
              yAxisId="left"
              type="monotone"
              dataKey="total_topup"
              name="TMT"
              stroke="#4ECDC4"
              fill="url(#gradient-tmt)"
              strokeWidth={2}
              connectNulls={true}
            />
          )}
          
          {/* Currency areas - conditional */}
          {currencies.map((cur, idx) => (
            visibleSeries[cur] !== false && (
              <Area
                key={cur}
                yAxisId="left"
                type="monotone"
                dataKey={`amount_${cur}`}
                name={cur}
                stroke={CURRENCY_COLORS[cur] || CHART_COLORS.gradient[idx % 5]}
                fill={`url(#gradient-${cur})`}
                strokeWidth={2}
                connectNulls={true}
              />
            )
          ))}
          
          {/* Orders line - conditional */}
          {visibleSeries.orders && (
            <Line
              yAxisId="right"
              type="monotone"
              dataKey="orders_count"
              name={t('Заказы')}
              stroke="#FF6B6B"
              strokeWidth={2}
              dot={false}
              connectNulls={true}
            />
          )}
        </ComposedChart>
      </ResponsiveContainer>
    </Box>
  );
}

function PaymentMethodChart({ data }) {
  const { t, lang } = useI18n();
  const numberLocale = lang === 'ru' ? 'ru-RU' : lang === 'tr' ? 'tr-TR' : 'en-US';
  const formatNum = (value) => formatNumber(value, numberLocale);

  if (!data?.length) {
    return (
      <Typography color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
        {t('Нет данных')}
      </Typography>
    );
  }

  const chartData = data.map((item, idx) => ({
    name: item.payment_method,
    orders: item.total_orders,
    topup: item.total_topup,
    fill: CHART_COLORS.gradient[idx % 5],
  }));

  return (
    <Box>
      <ResponsiveContainer width="100%" height={250}>
        <BarChart data={chartData} layout="vertical" margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#E8E8E8" />
          <XAxis type="number" tick={{ fontSize: 11, fill: '#888' }} tickFormatter={(v) => formatNum(v)} />
          <YAxis type="category" dataKey="name" tick={{ fontSize: 11, fill: '#888' }} width={100} />
          <RechartsTooltip
            contentStyle={{
              backgroundColor: 'rgba(255,255,255,0.95)',
              borderRadius: 12,
              border: '1px solid rgba(124, 77, 255, 0.2)',
            }}
            formatter={(value) => formatNum(value)}
          />
          <Bar dataKey="topup" name="TMT" radius={[0, 8, 8, 0]}>
            {chartData.map((entry, idx) => (
              <Cell key={idx} fill={entry.fill} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
      
      {/* Details list */}
      <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
        {data.map((item, idx) => (
          <Box
            key={item.payment_method}
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              p: 1.5,
              borderRadius: 2,
              bgcolor: `${CHART_COLORS.gradient[idx % 5]}11`,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box
                sx={{
                  width: 12,
                  height: 12,
                  borderRadius: '50%',
                  bgcolor: CHART_COLORS.gradient[idx % 5],
                }}
              />
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {item.payment_method}
              </Typography>
            </Box>
            <Box sx={{ textAlign: 'right' }}>
              <Typography variant="body2" sx={{ fontWeight: 700, color: '#333' }}>
                {formatNum(item.total_topup)} TMT
              </Typography>
              <Typography variant="caption" sx={{ color: '#888' }}>
                {formatNum(item.total_orders)} {t('Заказов')}
              </Typography>
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
}

function ServiceChart({ data }) {
  const { t, lang } = useI18n();
  const numberLocale = lang === 'ru' ? 'ru-RU' : lang === 'tr' ? 'tr-TR' : 'en-US';
  const formatNum = (value) => formatNumber(value, numberLocale);

  if (!data?.length) {
    return (
      <Typography color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
        {t('Нет данных')}
      </Typography>
    );
  }

  // Service-specific colors for better distinction
  const SERVICE_COLORS = {
    'terbium': '#7C4DFF',
    'bahbit': '#00BCD4',
    'raffle': '#FF5722',
    'topup': '#4CAF50',
    'default': '#9C27B0',
  };

  const getServiceColor = (serviceName, idx) => {
    const lowerName = serviceName?.toLowerCase() || '';
    for (const [key, color] of Object.entries(SERVICE_COLORS)) {
      if (lowerName.includes(key)) return color;
    }
    return CHART_COLORS.gradient[idx % 5];
  };

  const chartData = data.map((item, idx) => ({
    name: item.service,
    topup: item.total_topup,
    topupFreekassa: item.total_topup_freekassa || 0,
    topupStripe: item.total_topup_stripe || 0,
    topupWata: item.total_topup_wata || 0,
    orders: item.total_orders,
    fill: getServiceColor(item.service, idx),
  }));

  const totalOrders = chartData.reduce((sum, item) => sum + item.orders, 0);

  return (
    <Box>
      <ResponsiveContainer width="100%" height={250}>
        <BarChart data={chartData} layout="vertical" margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#E8E8E8" />
          <XAxis type="number" tick={{ fontSize: 11, fill: '#888' }} tickFormatter={(v) => formatNum(v)} />
          <YAxis type="category" dataKey="name" tick={{ fontSize: 11, fill: '#888' }} width={100} />
          <RechartsTooltip
            contentStyle={{
              backgroundColor: 'rgba(255,255,255,0.95)',
              borderRadius: 12,
              border: '1px solid rgba(124, 77, 255, 0.2)',
            }}
            formatter={(value) => [formatNum(value), t('Заказов')]}
          />
          <Bar dataKey="orders" name={t('Заказы')} radius={[0, 8, 8, 0]}>
            {chartData.map((entry, idx) => (
              <Cell key={idx} fill={entry.fill} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
      
      {/* Details list below the chart */}
      <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
        {chartData.map((item) => {
          const percent = totalOrders > 0 ? ((item.orders / totalOrders) * 100).toFixed(1) : 0;
          return (
            <Box
              key={item.name}
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                p: 1.5,
                borderRadius: 2,
                bgcolor: `${item.fill}11`,
                transition: 'all 0.2s ease',
                cursor: 'pointer',
                '&:hover': {
                  bgcolor: `${item.fill}22`,
                  transform: 'translateX(4px)',
                },
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Box
                  sx={{
                    width: 12,
                    height: 12,
                    borderRadius: '50%',
                    bgcolor: item.fill,
                    boxShadow: `0 0 8px ${item.fill}66`,
                  }}
                />
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {item.name}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mt: 0.5 }}>
                    <Typography variant="caption" sx={{ color: '#555', fontWeight: 600 }}>
                      {formatNum(item.topup)} TMT
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        color: '#7C4DFF',
                        fontWeight: 600,
                        backgroundColor: 'rgba(124, 77, 255, 0.08)',
                        px: 0.8,
                        py: 0.2,
                        borderRadius: 1,
                      }}
                    >
                      freekassa: {formatNum(item.topupFreekassa)} TMT
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        color: '#00BCD4',
                        fontWeight: 600,
                        backgroundColor: 'rgba(0, 188, 212, 0.08)',
                        px: 0.8,
                        py: 0.2,
                        borderRadius: 1,
                      }}
                    >
                      stripe: {formatNum(item.topupStripe)} TMT
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        color: '#4CAF50',
                        fontWeight: 600,
                        backgroundColor: 'rgba(76, 175, 80, 0.08)',
                        px: 0.8,
                        py: 0.2,
                        borderRadius: 1,
                      }}
                    >
                      wata: {formatNum(item.topupWata)} TMT
                    </Typography>
                  </Box>
                </Box>
              </Box>
              <Box sx={{ textAlign: 'right' }}>
                <Typography variant="body2" sx={{ fontWeight: 700, color: '#333' }}>
                  {formatNum(item.orders)} {t('Заказов')}
                </Typography>
                <Typography variant="caption" sx={{ color: item.fill, fontWeight: 600 }}>
                  {percent}%
                </Typography>
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}

function CompletionRatesChart({ data }) {
  const { t, lang } = useI18n();
  const numberLocale = lang === 'ru' ? 'ru-RU' : lang === 'tr' ? 'tr-TR' : 'en-US';
  const formatNum = (value) => formatNumber(value, numberLocale);

  if (!data?.by_service?.length) {
    return (
      <Typography color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
        {t('Нет данных')}
      </Typography>
    );
  }

  const { by_service, overall } = data;

  // Colors for the pie chart
  const STATUS_COLORS = {
    completed: '#4CAF50',
    unpaid: '#FF5722',
    paid: '#2196F3',
    cancelled: '#9E9E9E',
  };

  // Реальная конверсия: completed / (completed + unpaid)
  // Это показывает какой % заказов реально оплачен от тех, что должны были быть оплачены
  const relevantOrders = overall.completed_orders + overall.unpaid_orders;
  const realConversionRate = relevantOrders > 0 
    ? ((overall.completed_orders / relevantOrders) * 100).toFixed(1) 
    : 0;
  const lossRate = relevantOrders > 0 
    ? ((overall.unpaid_orders / relevantOrders) * 100).toFixed(1) 
    : 0;

  // Данные для главной диаграммы - только completed vs unpaid
  const conversionPieData = [
    { name: t('Оплачено'), value: overall.completed_orders, color: STATUS_COLORS.completed },
    { name: t('Не оплачено'), value: overall.unpaid_orders, color: STATUS_COLORS.unpaid },
  ].filter(item => item.value > 0);

  return (
    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', lg: 'row' }, gap: 3 }}>
      {/* Overall Conversion Chart */}
      <Box sx={{ 
        flex: '0 0 300px', 
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center',
        p: 2,
        borderRadius: 3,
        background: 'linear-gradient(135deg, rgba(139, 195, 74, 0.08) 0%, rgba(76, 175, 80, 0.04) 100%)',
      }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#555', mb: 1 }}>
          {t('Конверсия оплат')}
        </Typography>
        <Typography variant="caption" sx={{ color: '#888', mb: 1, textAlign: 'center' }}>
          {t('Оплачено vs Не оплачено')}
        </Typography>
        <Box sx={{ position: 'relative', width: 200, height: 200 }}>
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={conversionPieData}
                cx="50%"
                cy="50%"
                innerRadius={55}
                outerRadius={85}
                paddingAngle={4}
                dataKey="value"
                stroke="none"
              >
                {conversionPieData.map((entry, idx) => (
                  <Cell key={idx} fill={entry.color} />
                ))}
              </Pie>
              <RechartsTooltip
                contentStyle={{
                  backgroundColor: 'rgba(255,255,255,0.95)',
                  borderRadius: 12,
                  border: '1px solid rgba(0,0,0,0.1)',
                }}
                formatter={(value, name) => [formatNumber(value), name]}
              />
            </PieChart>
          </ResponsiveContainer>
          {/* Center label */}
          <Box sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            textAlign: 'center',
          }}>
            <Typography variant="h4" sx={{ fontWeight: 700, color: STATUS_COLORS.completed }}>
              {realConversionRate}%
            </Typography>
            <Typography variant="caption" sx={{ color: '#888' }}>
              {t('Конверсия')}
            </Typography>
          </Box>
        </Box>
        
        {/* Stats */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 2, width: '100%' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: STATUS_COLORS.completed }} />
              <Typography variant="body2" sx={{ color: '#666' }}>{t('Оплачено')}</Typography>
            </Box>
            <Typography variant="body2" sx={{ fontWeight: 700, color: STATUS_COLORS.completed }}>
              {formatNum(overall.completed_orders)}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: STATUS_COLORS.unpaid }} />
              <Typography variant="body2" sx={{ color: '#666' }}>{t('Не оплачено')}</Typography>
            </Box>
            <Typography variant="body2" sx={{ fontWeight: 700, color: STATUS_COLORS.unpaid }}>
              {formatNum(overall.unpaid_orders)} ({lossRate}%)
            </Typography>
          </Box>
          {overall.paid_orders > 0 && (
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 1, opacity: 0.7 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: STATUS_COLORS.paid }} />
                <Typography variant="body2" sx={{ color: '#666' }}>{t('В обработке')}</Typography>
              </Box>
              <Typography variant="body2" sx={{ color: '#666' }}>
                {formatNum(overall.paid_orders)}
              </Typography>
            </Box>
          )}
          {overall.cancelled_orders > 0 && (
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 1, opacity: 0.7 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: STATUS_COLORS.cancelled }} />
                <Typography variant="body2" sx={{ color: '#666' }}>{t('Отменено')}</Typography>
              </Box>
              <Typography variant="body2" sx={{ color: '#666' }}>
                {formatNum(overall.cancelled_orders)}
              </Typography>
            </Box>
          )}
        </Box>
      </Box>

      {/* By Service List */}
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#555', mb: 0.5 }}>
          {t('Конверсия по сервисам')}
        </Typography>
        {by_service.map((service) => {
          // Реальная конверсия: completed / (completed + unpaid)
          const serviceRelevant = service.completed_orders + service.unpaid_orders;
          const serviceConversion = serviceRelevant > 0 
            ? ((service.completed_orders / serviceRelevant) * 100).toFixed(1)
            : 0;
          const serviceLoss = serviceRelevant > 0 
            ? ((service.unpaid_orders / serviceRelevant) * 100).toFixed(1)
            : 0;
          
          const completionColor = serviceConversion >= 70 
            ? '#4CAF50' 
            : serviceConversion >= 40 
              ? '#FF9800' 
              : '#F44336';
          
          return (
            <Box
              key={service.service}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                p: 1.5,
                borderRadius: 2,
                bgcolor: 'rgba(0,0,0,0.02)',
                border: '1px solid rgba(0,0,0,0.05)',
                transition: 'all 0.2s',
                '&:hover': {
                  bgcolor: 'rgba(0,0,0,0.04)',
                  transform: 'translateX(4px)',
                },
              }}
            >
              {/* Mini progress ring */}
              <Box sx={{ position: 'relative', width: 50, height: 50, flexShrink: 0 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={[
                        { value: parseFloat(serviceConversion) },
                        { value: 100 - parseFloat(serviceConversion) },
                      ]}
                      cx="50%"
                      cy="50%"
                      innerRadius={16}
                      outerRadius={24}
                      startAngle={90}
                      endAngle={-270}
                      dataKey="value"
                      stroke="none"
                    >
                      <Cell fill={completionColor} />
                      <Cell fill="#E0E0E0" />
                    </Pie>
                  </PieChart>
                </ResponsiveContainer>
                <Typography
                  sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    fontSize: 10,
                    fontWeight: 700,
                    color: completionColor,
                  }}
                >
                  {serviceConversion}%
                </Typography>
              </Box>

              {/* Service info */}
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, color: '#333' }}>
                  {service.service}
                </Typography>
                <Typography variant="caption" sx={{ color: '#888' }}>
                  {t('Оплачено')}: {formatNum(service.completed_orders)} {t('из')} {formatNum(serviceRelevant)}
                </Typography>
              </Box>

              {/* Stats */}
              <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
                <Typography variant="body2" sx={{ fontWeight: 700, color: completionColor }}>
                  {serviceConversion}%
                </Typography>
                <Typography variant="caption" sx={{ color: STATUS_COLORS.unpaid }}>
                  {serviceLoss}% {t('потеряно')}
                </Typography>
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}

function CurrencyTable({ data }) {
  const { t, lang } = useI18n();
  const numberLocale = lang === 'ru' ? 'ru-RU' : lang === 'tr' ? 'tr-TR' : 'en-US';
  const formatNum = (value) => formatNumber(value, numberLocale);
  const formatCurr = (value, currency) => formatCurrency(value, currency, numberLocale);

  if (!data?.length) {
    return (
      <Typography color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
        {t('Нет данных')}
      </Typography>
    );
  }

  return (
    <TableContainer>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 700, color: '#555' }}>{t('Валюта')}</TableCell>
            <TableCell align="right" sx={{ fontWeight: 700, color: '#555' }}>{t('Заказов')}</TableCell>
            <TableCell align="right" sx={{ fontWeight: 700, color: '#555' }}>{t('Сумма')}</TableCell>
            <TableCell align="right" sx={{ fontWeight: 700, color: '#555' }}>{t('Пополнено (TMT)')}</TableCell>
            <TableCell align="right" sx={{ fontWeight: 700, color: '#555' }}>{t('Билетов')}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {data.map((row) => (
            <TableRow
              key={row.currency}
              sx={{
                '&:hover': {
                  bgcolor: 'rgba(124, 77, 255, 0.04)',
                },
              }}
            >
              <TableCell>
                <Chip
                  label={row.currency}
                  size="small"
                  sx={{
                    backgroundColor: CURRENCY_COLORS[row.currency] || '#7C4DFF',
                    color: '#fff',
                    fontWeight: 700,
                  }}
                />
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 600 }}>
                {formatNum(row.orders_count)}
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 600 }}>
                {formatCurr(row.total_amount, row.currency)}
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 700, color: '#7C4DFF' }}>
                {formatNum(row.total_topup)}
              </TableCell>
              <TableCell align="right">
                {formatNum(row.total_tickets)}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

